/**
 * Extracts Wi-Fi credentials from a decoded string. This function assumes the string will follow a structure like:
 *
 * WIFI:S:<ssid>;T:<security>;P:<password>;;
 *
 * Where the order of S:, T: and P: can change.
 *
 * This function also assumes that the values for SSID, password and security method do not include characters 'S:',
 * 'T:' or 'P:'.
 *
 * @param decodedCredentials - Wi-Fi credentials string decoded from a QR code
 * @returns An object containing the ssid and password extracted from the provided string.
 */
export function extractWifiCredentials(decodedCredentials: string) {
  const split = decodedCredentials.split(';');
  const ssidSegment = split.find((substring) => substring.includes('S:'));
  const passwordSegment = split.find((substring) => substring.includes('P:'));
  const hiddenSegment = split.find((substring) => substring.includes('H:'));

  if (!ssidSegment || !passwordSegment) throw new Error(`Invalid wifi credentials string supplied: ${split}`);

  const ssid = ssidSegment.split('S:')[1]?.trim();
  const password = passwordSegment.split('P:')[1]?.trim();
  const isHiddenSsid = hiddenSegment ? hiddenSegment.split('H:')[1]?.trim().toLowerCase() === 'true' : false;

  if (!ssid || !password) throw new Error('Invalid QR code');

  return {
    ssid,
    password,
    isHiddenSsid,
  };
}

/**
 * Maps a hardware signal strength or connection status to a numeric value.
 *
 * @param key - The hardware signal strength or connection status to be evaluated.
 * @returns A numeric value representing the strength or connection status.
 */
export function getStrengthValue(key?: string) {
  if (key === 'Marginal') return 1;
  if (key === 'OK') return 2;
  if (key === 'Good') return 3;
  if (key === 'Excellent' || key === 'Connected') return 4;
  return 0;
}
